/*
 * AdvanceForm.tsx : Un formulaire pour ajouter ou modifier un acompte.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faMoneyBills,
	faInfoCircle,
	faLink,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	finishFormSave,
	FormSaveActions,
	InputField,
	SelectField,
	useFormDirty,
} from '../../../common';
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import Advance
import { AdvanceData } from '../types/types';
import { advancesApi } from '../services/advancesApi';
import { projectsApi } from 'features/projects/services/projectsApi';
import { customerInvoicesApi } from 'features/customerInvoices/services/customerInvoicesApi';

// Composant AdvanceForm
const AdvanceForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const userId = localStorage.getItem('userId');
	const userName = localStorage.getItem('name');
	const userFirstName = localStorage.getItem('firstName');
	const [formData, setFormData] = React.useState<AdvanceData>({
		id: 0,
		referenceNumber: '',
		amount: 0,
		project: null,
		customerInvoice: null,
		createdBy: userId
			? {
					id: Number(userId),
					name: userName || '',
					firstName: userFirstName || '',
				}
			: undefined,
		createdAt: new Date()
			.toISOString()
			.replace('Z', '+00:00') as unknown as Date,
	});

	// Hooks select project
	const [projectOptions, setProjectOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingProjects, setLoadingProjects] = React.useState(true);

	// Hooks select customerInvoice
	const [customerInvoiceOptions, setCustomerInvoiceOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingCustomerInvoices, setLoadingCustomerInvoices] =
		React.useState(true);

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Hook pour le prochain numéro de référence
	const [nextRef, setNextRef] = React.useState<string | null>(null);

	// Une fois le composant monté et si un id est présent, fetch des données et préremplissage du formulaire
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		if (id) {
			advancesApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					// Préremplir les selects
					if (data.project) {
						setProjectOptions([
							{
								key: data.project.id.toString(),
								label: data.project.referenceNumber,
							},
						]);
					}
					if (data.customerInvoice) {
						setCustomerInvoiceOptions([
							{
								key: data.customerInvoice.id.toString(),
								label: data.customerInvoice.referenceNumber,
							},
						]);
					}
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
		}
	}, [id]);

	// Lazy load des options projets en arrière-plan (route /select allégée)
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingProjects) {
			projectsApi
				.listSelect(token)
				.then((items) => {
					setProjectOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingProjects(false));
		}
	}, [loading, loadingProjects]);

	// Lazy load des options factures clients en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingCustomerInvoices) {
			customerInvoicesApi
				.listSelect(token)
				.then((items) => {
					setCustomerInvoiceOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingCustomerInvoices(false));
		}
	}, [loading, loadingCustomerInvoices]);

	// Lazy load du prochain numéro de référence
	React.useEffect(() => {
		// Vérification de la création d'une nouvelle commande
		if (id) return;

		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si on n'a pas déjà récupéré la prochaine référence
		if (!loading && nextRef === null) {
			// Récupération de l'année en cours
			const year = new Date().getFullYear();

			// Fetch du prochain numéro de référence
			advancesApi
				.previewReference(token, year)
				.then((data) => {
					setNextRef(data.nextReference);
					// Mise à jour du numéro de référence dans le formulaire
					setFormData((prevData) => ({
						...prevData,
						referenceNumber: 'AC' + data.nextReference,
					}));
					setBaselineReady(true);
				})
				.catch((error) => {
					console.error(error);
					setBaselineReady(true);
				});
		}
	}, [loading]);

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/advances');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			advancesApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			advancesApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/advances/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion des changements dans le formulaire pour les selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'project') {
			const selectedOption = projectOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					project: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'customerInvoice') {
			const selectedOption = customerInvoiceOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					customerInvoice: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Rendu du composant, le premier div est la card principale
	return (
		<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
			{/* HEADER DE LA CARD */}
			<CardHeader
				icon={faMoneyBills}
				title={id ? "Édition d'un acompte" : 'Nouveau acompte'}
				createdAt={
					formData.createdAt ? formData.createdAt?.toLocaleString() : undefined
				}
				createdBy={
					formData.createdBy
						? formData.createdBy.firstName + ' ' + formData.createdBy.name
						: undefined
				}
				loading={loading}
			/>

			{/* CONTENU DE LA CARD */}
			<form onSubmit={handleSubmit} className="space-y-6">
				{/* 2 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* COLONNE GAUCHE */}
					<CardSection icon={faInfoCircle} title="Informations générales">
						<InputField
							label="Numéro de référence"
							name="referenceNumber"
							value={formData.referenceNumber}
							onChange={handleChange}
							loading={loading}
							placeholder="Chargement..."
							readOnly={id ? false : true}
						/>
						<InputField
							label="Montant"
							name="amount"
							type="number"
							value={formData.amount}
							onChange={handleChange}
							loading={loading}
							placeholder="Ex : 1500.00"
						/>
					</CardSection>

					{/* COLONNE DROITE */}
					<CardSection icon={faLink} title="Autres liens">
						<SelectField
							label="Affaire"
							selectedKey={formData.project?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'project',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={projectOptions}
							loading={loading}
							optionsLoading={loadingProjects}
							entityHref={
								formData.project?.id
									? `/app/projects/${formData.project.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									project: null,
								}))
							}
						/>
						<SelectField
							label="Facture client"
							selectedKey={formData.customerInvoice?.id?.toString() || ''}
							onValueChange={(val) =>
								handleChangeSelect({
									target: {
										name: 'customerInvoice',
										value: val,
									},
								} as React.ChangeEvent<HTMLSelectElement>)
							}
							options={customerInvoiceOptions}
							loading={loading}
							optionsLoading={loadingCustomerInvoices}
							entityHref={
								formData.customerInvoice?.id
									? `/app/customerInvoices/${formData.customerInvoice.id}/edit`
									: undefined
							}
							onClear={() =>
								setFormData((prev) => ({
									...prev,
									customerInvoice: null,
								}))
							}
						/>
					</CardSection>
				</div>
			</form>

			<FormSaveActions
				isDirty={isDirty}
				isSubmitting={isSubmitting}
				onCancel={() => navigate('/app/advances')}
				onSave={() => handleSave(false)}
				onSaveAndLeave={() => handleSave(true)}
			/>
		</div>
	);
};

export default AdvanceForm;
